/**
 * Activity Log Service
 * 
 * Comprehensive audit logging for all significant actions in the system.
 * Non-blocking, fails gracefully to prevent disruption of core operations.
 * Supports real-time activity streams with role-based visibility.
 */

import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ActivityLogEntry,
  ActivityAction,
  ActivityTargetType,
  ActivityVisibility,
  ActivityMetadata,
  ActivityStats,
  ActivityFilters,
  ActivityLogResponse,
} from '../types/activity-log';
import { User } from '../types/content';

export class ActivityLogService {
  private static collectionName = 'activity_log';

  /**
   * Log an activity (non-blocking, fails gracefully)
   * This is the primary method called throughout the application
   */
  static async logActivity(
    action: ActivityAction,
    actor: User,
    targetType: ActivityTargetType,
    targetId: string,
    targetTitle?: string,
    metadata?: ActivityMetadata,
    visibility?: ActivityVisibility
  ): Promise<void> {
    try {
      if (!db) {
        return; // Silently skip if no database
      }

      if (!actor?.id) {
        return; // Silently skip if no actor ID
      }

      // Determine visibility if not explicitly provided
      const activityVisibility = visibility || this.determineVisibility(action, actor.role);

      const activityEntry: Omit<ActivityLogEntry, 'id'> = {
        timestamp: new Date(),
        action,
        actorId: actor.id,
        actorName: actor.displayName,
        actorRole: actor.role,
        targetType,
        targetId,
        targetTitle,
        metadata: metadata || {},
        visibility: activityVisibility,
      };

      await addDoc(collection(db, this.collectionName), {
        ...activityEntry,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      // CRITICAL: Never throw - logging failures should not disrupt operations
      // Fail completely silently to prevent any app disruption
    }
  }

  /**
   * Determine appropriate visibility based on action type and actor role
   */
  private static determineVisibility(action: ActivityAction, role: string): ActivityVisibility {
    // Owner-only actions
    const ownerOnlyActions: ActivityAction[] = [
      'user_deleted',
      'user_restored',
      'user_role_changed',
    ];

    // Admin-level actions
    const adminActions: ActivityAction[] = [
      'article_deleted',
      'article_restored',
      'user_created',
      'user_deactivated',
      'user_activated',
      'article_revision_requested',
      'article_rejected',
      'tag_assigned',
      'tag_removed',
    ];

    // Public actions (visible to all users)
    const publicActions: ActivityAction[] = [
      'article_created',
      'article_published',
      'user_created',
      'application_submitted',
      'application_approved',
      'tag_created',
    ];

    if (ownerOnlyActions.includes(action)) {
      return 'owner_only';
    } else if (adminActions.includes(action)) {
      return 'admin';
    } else if (publicActions.includes(action)) {
      return 'public';
    }

    // Default: admin visibility for unspecified actions
    return 'admin';
  }

  /**
   * Get recent activity with role-based filtering
   * @param context - 'dashboard' shows public only, 'activity_log' shows all based on role
   */
  static async getRecentActivity(
    currentUser: User,
    limitCount: number = 50,
    context: 'dashboard' | 'activity_log' = 'dashboard'
  ): Promise<ActivityLogEntry[]> {
    try {
      if (!db) throw new Error('Database not initialized');

      const constraints: QueryConstraint[] = [];

      // Context-aware visibility filtering
      if (context === 'dashboard') {
        // Dashboard stream: ONLY public celebrations (clean feed for everyone)
        constraints.push(where('visibility', '==', 'public'));
      } else {
        // Activity Log page: Role-based full visibility
        if (currentUser.role === 'owner') {
          // Owners see everything - no filter needed (all visibility levels)
        } else if (currentUser.role === 'admin') {
          // Admins see admin, admin_audit, and public (not owner_only)
          constraints.push(where('visibility', 'in', ['admin', 'admin_audit', 'public']));
        } else {
          // Writers and editors see only public activities
          constraints.push(where('visibility', '==', 'public'));
        }
      }

      constraints.push(orderBy('timestamp', 'desc'));
      constraints.push(firestoreLimit(limitCount));

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
        } as ActivityLogEntry;
      });
    } catch (error) {
      console.error('❌ Failed to get recent activity:', error);
      return [];
    }
  }

  /**
   * Get dashboard activity stream (optimized for real-time widget)
   * Always shows only PUBLIC events - gives admins/owners the user perspective
   */
  static async getDashboardStream(
    currentUser: User,
    limitCount: number = 50
  ): Promise<ActivityLogEntry[]> {
    // Always use 'dashboard' context for dashboard widget (PUBLIC only)
    return this.getRecentActivity(currentUser, limitCount, 'dashboard');
  }

  /**
   * Subscribe to real-time activity updates
   * Returns unsubscribe function
   * @param context - 'dashboard' shows public only, 'activity_log' shows all based on role
   */
  static subscribeToActivity(
    currentUser: User,
    callback: (activities: ActivityLogEntry[]) => void,
    limitCount: number = 50,
    context: 'dashboard' | 'activity_log' = 'dashboard'
  ): Unsubscribe {
    if (!db) {
      console.warn('⚠️ Database not initialized, cannot subscribe to activity');
      return () => {}; // Return no-op unsubscribe
    }

    const constraints: QueryConstraint[] = [];

    // Context-aware visibility filtering (SAME as getRecentActivity)
    if (context === 'dashboard') {
      // Dashboard stream: ONLY public celebrations (clean feed for EVERYONE including owners!)
      constraints.push(where('visibility', '==', 'public'));
    } else {
      // Activity Log page: Role-based full visibility
      if (currentUser.role === 'owner') {
        // Owners see everything - no filter needed (all visibility levels)
      } else if (currentUser.role === 'admin') {
        // Admins see admin, admin_audit, and public (not owner_only)
        constraints.push(where('visibility', 'in', ['admin', 'admin_audit', 'public']));
      } else {
        // Writers and editors see only public activities
        constraints.push(where('visibility', '==', 'public'));
      }
    }

    constraints.push(orderBy('timestamp', 'desc'));
    constraints.push(firestoreLimit(limitCount));

    const q = query(collection(db, this.collectionName), ...constraints);

    return onSnapshot(
      q,
      (querySnapshot) => {
        const activities: ActivityLogEntry[] = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
          } as ActivityLogEntry;
        });
        callback(activities);
      },
      (error) => {
        console.error('❌ Error in activity subscription:', error);
        callback([]); // Return empty array on error
      }
    );
  }

  /**
   * Get all actions by a specific user
   */
  static async getActivityByUser(
    userId: string,
    currentUser: User,
    limitCount: number = 100
  ): Promise<ActivityLogEntry[]> {
    try {
      if (!db) throw new Error('Database not initialized');

      const constraints: QueryConstraint[] = [
        where('actorId', '==', userId),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limitCount),
      ];

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
          } as ActivityLogEntry;
        })
        .filter((activity) => this.canUserSeeActivity(activity, currentUser));
    } catch (error) {
      console.error('❌ Failed to get activity by user:', error);
      return [];
    }
  }

  /**
   * Get all actions for a specific target (article, user, etc)
   */
  static async getActivityByTarget(
    targetType: ActivityTargetType,
    targetId: string,
    currentUser: User,
    limitCount: number = 50
  ): Promise<ActivityLogEntry[]> {
    try {
      if (!db) throw new Error('Database not initialized');

      const constraints: QueryConstraint[] = [
        where('targetType', '==', targetType),
        where('targetId', '==', targetId),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limitCount),
      ];

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
          } as ActivityLogEntry;
        })
        .filter((activity) => this.canUserSeeActivity(activity, currentUser));
    } catch (error) {
      console.error('❌ Failed to get activity by target:', error);
      return [];
    }
  }

  /**
   * Get paginated activity log with advanced filtering
   */
  static async getFilteredActivity(
    filters: ActivityFilters,
    currentUser: User
  ): Promise<ActivityLogResponse> {
    try {
      if (!db) throw new Error('Database not initialized');

      const constraints: QueryConstraint[] = [];

      // Apply role-based visibility
      if (currentUser.role === 'owner') {
        // Owners see everything
        if (filters.visibilities && filters.visibilities.length > 0) {
          constraints.push(where('visibility', 'in', filters.visibilities));
        }
      } else if (currentUser.role === 'admin') {
        const allowedVisibilities = ['admin', 'admin_audit', 'public'];
        const requestedVisibilities = filters.visibilities?.filter((v) =>
          allowedVisibilities.includes(v)
        );
        if (requestedVisibilities && requestedVisibilities.length > 0) {
          constraints.push(where('visibility', 'in', requestedVisibilities));
        } else {
          constraints.push(where('visibility', 'in', allowedVisibilities));
        }
      } else {
        constraints.push(where('visibility', '==', 'public'));
      }

      // Apply additional filters
      if (filters.actorIds && filters.actorIds.length > 0) {
        constraints.push(where('actorId', 'in', filters.actorIds.slice(0, 10)));
      }

      if (filters.targetTypes && filters.targetTypes.length > 0) {
        constraints.push(where('targetType', 'in', filters.targetTypes));
      }

      // Always order by timestamp
      constraints.push(orderBy('timestamp', 'desc'));

      // Apply limit
      const limitCount = filters.limit || 50;
      constraints.push(firestoreLimit(limitCount + 1)); // Get one extra to check hasMore

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      const activities: ActivityLogEntry[] = querySnapshot.docs
        .slice(0, limitCount)
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
          } as ActivityLogEntry;
        });

      // Apply client-side filters that can't be done in Firestore
      let filteredActivities = activities;

      if (filters.actions && filters.actions.length > 0) {
        filteredActivities = filteredActivities.filter((activity) =>
          filters.actions!.includes(activity.action)
        );
      }

      if (filters.startDate) {
        filteredActivities = filteredActivities.filter(
          (activity) => activity.timestamp >= filters.startDate!
        );
      }

      if (filters.endDate) {
        filteredActivities = filteredActivities.filter(
          (activity) => activity.timestamp <= filters.endDate!
        );
      }

      return {
        activities: filteredActivities,
        total: filteredActivities.length,
        hasMore: querySnapshot.docs.length > limitCount,
        nextOffset: (filters.offset || 0) + limitCount,
      };
    } catch (error) {
      console.error('❌ Failed to get filtered activity:', error);
      return {
        activities: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  /**
   * Get activity statistics for dashboard
   */
  static async getActivityStats(currentUser: User): Promise<ActivityStats> {
    try {
      if (!db) throw new Error('Database not initialized');

      // Get last 7 days of activity
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const activities = await this.getRecentActivity(currentUser, 500);

      const actionsByType: { [action: string]: number } = {};
      const actionsByDay: { [date: string]: number } = {};
      const userActionCounts: { [userId: string]: { name: string; count: number } } = {};

      activities.forEach((activity) => {
        // Count by type
        actionsByType[activity.action] = (actionsByType[activity.action] || 0) + 1;

        // Count by day
        const dateKey = activity.timestamp.toISOString().split('T')[0];
        actionsByDay[dateKey] = (actionsByDay[dateKey] || 0) + 1;

        // Count by user
        if (!userActionCounts[activity.actorId]) {
          userActionCounts[activity.actorId] = {
            name: activity.actorName,
            count: 0,
          };
        }
        userActionCounts[activity.actorId].count++;
      });

      // Get most active users
      const mostActiveUsers = Object.entries(userActionCounts)
        .map(([userId, data]) => ({
          userId,
          userName: data.name,
          actionCount: data.count,
        }))
        .sort((a, b) => b.actionCount - a.actionCount)
        .slice(0, 5);

      // Get recent trends (last 24 hours)
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      const recentActivities = activities.filter((a) => a.timestamp >= oneDayAgo);

      const recentTrends = {
        articlesCreated24h: recentActivities.filter((a) => a.action === 'article_created').length,
        articlesPublished24h: recentActivities.filter((a) => a.action === 'article_published')
          .length,
        usersJoined24h: recentActivities.filter((a) => a.action === 'user_created').length,
        applicationsSubmitted24h: recentActivities.filter(
          (a) => a.action === 'application_submitted'
        ).length,
      };

      return {
        totalActions: activities.length,
        actionsByType,
        actionsByDay: Object.entries(actionsByDay)
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => a.date.localeCompare(b.date)),
        mostActiveUsers,
        recentTrends,
      };
    } catch (error) {
      console.error('❌ Failed to get activity stats:', error);
      return {
        totalActions: 0,
        actionsByType: {},
        actionsByDay: [],
        mostActiveUsers: [],
        recentTrends: {
          articlesCreated24h: 0,
          articlesPublished24h: 0,
          usersJoined24h: 0,
          applicationsSubmitted24h: 0,
        },
      };
    }
  }

  /**
   * Check if user can see a specific activity
   */
  private static canUserSeeActivity(activity: ActivityLogEntry, user: User): boolean {
    if (user.role === 'owner') return true;
    if (user.role === 'admin' && (
      activity.visibility === 'admin' || 
      activity.visibility === 'admin_audit' || 
      activity.visibility === 'public'
    )) return true;
    if (activity.visibility === 'public') return true;
    return false;
  }

  /**
   * Helper: Log article creation
   */
  static async logArticleCreated(actor: User, articleId: string, articleTitle: string): Promise<void> {
    await this.logActivity(
      'article_created',
      actor,
      'article',
      articleId,
      articleTitle,
      { articleStatus: 'draft' },
      'public'
    );
  }

  /**
   * Helper: Log article publication
   */
  static async logArticlePublished(actor: User, articleId: string, articleTitle: string): Promise<void> {
    await this.logActivity(
      'article_published',
      actor,
      'article',
      articleId,
      articleTitle,
      { articleStatus: 'published' },
      'public'
    );
  }

  /**
   * Helper: Log article deletion
   */
  static async logArticleDeleted(
    actor: User,
    articleId: string,
    articleTitle: string,
    reason?: string
  ): Promise<void> {
    await this.logActivity(
      'article_deleted',
      actor,
      'article',
      articleId,
      articleTitle,
      { reason, daysUntilPermanent: 90 },
      'admin'
    );
  }

  /**
   * Helper: Log article restoration
   */
  static async logArticleRestored(
    actor: User,
    articleId: string,
    articleTitle: string
  ): Promise<void> {
    await this.logActivity(
      'article_restored',
      actor,
      'article',
      articleId,
      articleTitle,
      { restoredFrom: 'trash' },
      'admin'
    );
  }

  /**
   * Helper: Log user creation
   */
  static async logUserCreated(actor: User, userId: string, userName: string, userRole: string): Promise<void> {
    await this.logActivity(
      'user_created',
      actor,
      'user',
      userId,
      userName,
      { newRole: userRole },
      'public'
    );
  }

  /**
   * Helper: Log user activation/deactivation
   */
  static async logUserStatusChange(
    actor: User,
    userId: string,
    userName: string,
    isActive: boolean
  ): Promise<void> {
    await this.logActivity(
      isActive ? 'user_activated' : 'user_deactivated',
      actor,
      'user',
      userId,
      userName,
      {},
      'owner_only'
    );
  }

  /**
   * Change visibility of an activity entry (Owner/Admin only)
   * Useful for promoting audit events to public or hiding sensitive info
   * 
   * @example
   * // Make a tag rejection public (share the learning moment)
   * await activityLogService.changeVisibility(activityId, 'public', ownerUser);
   * 
   * // Hide a public event (move to audit)
   * await activityLogService.changeVisibility(activityId, 'admin_audit', adminUser);
   */
  static async changeVisibility(
    activityId: string,
    newVisibility: ActivityVisibility,
    actor: User
  ): Promise<void> {
    try {
      if (!db) throw new Error('Database not initialized');

      // Only owners and admins can change visibility
      if (actor.role !== 'owner' && actor.role !== 'admin') {
        throw new Error('Only owners and admins can change activity visibility');
      }

      // Get the activity entry
      const activityRef = doc(db, this.collectionName, activityId);
      const activityDoc = await getDoc(activityRef);

      if (!activityDoc.exists()) {
        throw new Error('Activity not found');
      }

      const oldVisibility = activityDoc.data().visibility;

      // Update visibility
      await updateDoc(activityRef, {
        visibility: newVisibility,
        visibilityChangedAt: serverTimestamp(),
        visibilityChangedBy: actor.id,
        previousVisibility: oldVisibility,
      });

      // Log the visibility change itself (meta-logging)
      await this.logActivity(
        'user_profile_updated', // Reuse existing action type
        actor,
        'user', // Use 'user' as target type (closest match for admin actions)
        activityId,
        `Activity visibility changed`,
        {
          oldVisibility,
          newVisibility,
          targetActivityId: activityId,
        },
        'owner_only' // Visibility changes are sensitive
      );
    } catch (error) {
      console.error('ActivityLogService.changeVisibility error:', error);
      throw error;
    }
  }
}

// Export singleton-style (though it's all static methods)
export const activityLogService = ActivityLogService;

